import { jsonToQueryString } from '@fastlessjs/backoffice-layer/dist/helpers/strings';
import {
  paginationWithSelect,
  request,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import {
  APIResponse,
  ApiResponsePagination,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/types';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { APIOptions } from '../../types';
import { stripLeadingZerosForApi } from '../../utils';
import { Order, OrderLine, Orders } from '../types';

const normalizeOrderLines = (lignes: Order['lignes'] | undefined): OrderLine[] => {
  const ligne = lignes?.ligne;
  if (Array.isArray(ligne)) return ligne;
  if (ligne) return [ligne];
  return [];
};

const normalizeOrder = (order: Order | undefined): Order | undefined => {
  if (!order) return undefined;
  return {
    ...order,
    lignes: { ligne: normalizeOrderLines(order.lignes) },
  };
};

// Register this module for debug tracking
debug.registerComponent('orders-hooks');

/**
 * REQUESTS
 */

const basePath = '/orders/EPI';

const getOrder = async (options: APIOptions) => {
  const clientId = stripLeadingZerosForApi(options.clientId);
  const orderId = stripLeadingZerosForApi(options.orderId);
  debug.log('orders-hooks', `Fetching order: ${orderId} for client: ${clientId}`);

  try {
    const result = await request(`${basePath}/${clientId}/order/${orderId}`, {
      method: 'get',
      disableNotif: true, // We handle errors in the UI with OrderNotFound component
    });
    debug.log('orders-hooks', `Order fetched successfully: ${orderId}`);
    return result;
  } catch (error) {
    debug.error('orders-hooks', `Error fetching order ${orderId}: ${error}`);
    throw error;
  }
};

const getOrders = async (
  pageParam: ApiResponsePagination = { pageSize: 60 },
  options?: APIOptions,
) => {
  const tenantId = stripLeadingZerosForApi(options?.tenantId) ?? options?.tenantId;
  const qs = jsonToQueryString(pageParam);
  debug.log('orders-hooks', `Fetching orders for tenant: ${tenantId}`);

  try {
    const result = await request(`${basePath}/${tenantId}/${qs && `?${qs}`}`, {
      method: 'get',
      options: {
        headers: {
          'x-tenant-id': tenantId ?? '',
        },
      },
    });
    debug.log('orders-hooks', `Orders fetched successfully for tenant: ${tenantId}`);
    return result;
  } catch (error) {
    debug.error('orders-hooks', `Error fetching orders for tenant ${tenantId}: ${error}`);
    throw error;
  }
};

/**
 * HOOKS
 */

const useOrder = (options: APIOptions) => {
  debug.trackAccess('useOrder', 'hook execution');
  debug.log(
    'useOrder',
    `Hook called with orderId: ${options?.orderId}, clientId: ${options?.clientId}`,
  );

  return useQuery({
    queryKey: ['orders', options?.orderId, options?.clientId],
    queryFn: () =>
      getOrder(options).catch((err) => {
        debug.error('useOrder', `Query error: ${err}`);
        // Re-throw to let react-query handle it, but mark as handled for UI
        const orderError = Object.assign(err, { isOrderError: true });
        throw orderError;
      }),
    select: (response: APIResponse<Order>) => {
      debug.log('useOrder', `Selecting order data from response`);
      return normalizeOrder(response.data?.[0]);
    },
    enabled: !!options?.orderId,
    retry: false,
  });
};

const useOrders = (options?: APIOptions) => {
  debug.trackAccess('useOrders', 'hook execution');
  const { clientId } = options || {};
  debug.log('useOrders', `Hook called with clientId: ${clientId}`);

  return useInfiniteQuery({
    queryKey: ['orders', clientId?.toString()],
    queryFn: ({ pageParam }) =>
      getOrders(pageParam as ApiResponsePagination | undefined, options),
    enabled: !!clientId,
    initialPageParam: undefined,
    ...paginationWithSelect<Orders>(),
  });
};

export { useOrder, useOrders };

// Mark this module as initialized
debug.markInitialized('orders-hooks');
